import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { trackEvent } from '../components/Analytics';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.chaching.focus';
const APP_STORE_URL = 'https://apps.apple.com/ca/app/bucktime/id6760778797';

function StoreButtons({ src }) {
  return (
    <div className="lp-hero-btns">
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="lp-play-badge"
        onClick={() => trackEvent(`app-store-click-${src}`, '/')}
        data-testid={`${src}-appstore-btn`}
      >
        <img src="/app-store-badge.svg" alt="Download on the App Store" className="play-badge-img" style={{width: '200px', height: '60px', objectFit: 'contain'}} />
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="lp-play-badge"
        onClick={() => trackEvent(`play-store-click-${src}`, '/')}
        data-testid={`${src}-play-btn`}
      >
        <img src="/google-play-badge-trimmed.png" alt="Get it on Google Play" className="play-badge-img" style={{width: '200px', height: '60px', objectFit: 'contain'}} />
      </a>
    </div>
  );
}

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  };

  const [paused, setPaused] = useState(false);
  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setPaused(false);
      } else {
        videoRef.current.pause();
        setPaused(true);
      }
    }
  };

  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  const faqs = [
    {
      q: 'Where does the money come from?',
      a: "From you. It's the allowance you choose to give. BuckTime! ties it to screen time, so your child earns it by staying under their limit.",
    },
    {
      q: 'Does BuckTime! lock my child out of their device?',
      a: 'No. Your child decides. BuckTime! makes less screen time worth something to them.',
    },
    {
      q: 'Which devices does it work on?',
      a: "You use the app on iPhone, iPad or Android. Your child's phones, tablets and Windows or Mac computers can all count toward their screen time. You add each one from the app.",
    },
  ];

  return (
    <div className="landing-page" data-testid="landing-page">
      <SiteHeader />

      {/* ───── HERO ───── */}
      <section className="lp-hero" data-testid="hero-section">
        <div className="container lp-hero-inner lp-hero-inner--stacked">
          <h1 className="lp-hero-h1" data-testid="hero-headline">
            You fund it. They earn it.
          </h1>
          <p className="lp-hero-sub">
            BuckTime! helps families reduce screen time without blocking or locking anything. Parents set a daily screen time limit and an allowance. Kids keep their allowance by staying under the limit, so putting the screen down becomes their choice, not a rule enforced by the parent.
          </p>
          <p className="lp-hero-sub">
            Set a daily screen time limit. Stay under it, they keep it all. Go over a bit, they keep some. Go over a lot, they keep none.
          </p>
          <StoreButtons src="hero" />
          <p className="lp-hero-fine">
            Free to download for iPhone, iPad and Android. Screen time on your child's PC or Mac counts too.
          </p>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="lp-section lp-how" id="how-it-works" data-testid="how-section">
        <div className="container lp-how-layout">
          {/* Video on the left */}
          <div className="lp-how-video" data-testid="hero-video-frame">
            <div className="lp-phone-bezel" onClick={togglePlay} style={{cursor: 'pointer'}}>
              <video
                ref={videoRef}
                className="lp-phone-video"
                src="/bucktime-video.mp4"
                poster="/bucktime-video-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                data-testid="hero-video"
              />
              {paused && (
                <div className="lp-play-overlay" data-testid="video-play-overlay">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </div>
              )}
              <button
                className="lp-mute-btn"
                onClick={toggleMute}
                aria-label={muted ? 'Unmute video' : 'Mute video'}
                data-testid="video-mute-btn"
              >
                {muted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                )}
              </button>
            </div>
          </div>
          {/* Steps on the right */}
          <div className="lp-how-content">
            <h2 className="lp-section-h2">How BuckTime! works</h2>
            <div className="lp-steps lp-steps--vertical">
              <div className="lp-step" data-testid="step-1">
                <span className="lp-step-num">1</span>
                <h3 className="lp-step-title">You set the allowance.</h3>
                <p className="lp-step-desc">Choose how much your child can earn each day. It's the allowance you already give, with a purpose.</p>
              </div>
              <div className="lp-step" data-testid="step-2">
                <span className="lp-step-num">2</span>
                <h3 className="lp-step-title">You set the limit.</h3>
                <p className="lp-step-desc">Pick a daily screen time limit and choose which apps and devices count, including your child's PC or Mac.</p>
              </div>
              <div className="lp-step" data-testid="step-3">
                <span className="lp-step-num">3</span>
                <h3 className="lp-step-title">They make the choice.</h3>
                <p className="lp-step-desc">Every day, your child decides: more screen time, or more money. Less screen time, more money.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── WHY PARENTS USE IT ───── */}
      <section className="lp-section lp-why" data-testid="why-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">No more screen time battles</h2>
          <p className="lp-section-body">
            No nagging. No arguments. No taking devices away.
          </p>
          <p className="lp-section-body">
            BuckTime! puts the choice in your child's hands, so putting the screen down becomes their idea, not yours.
          </p>
        </div>
      </section>

      {/* ───── MONEY HABITS ───── */}
      <section className="lp-section lp-money" data-testid="money-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Money lessons that stick</h2>
          <p className="lp-section-body">
            Kids see what they've earned in real time, and they can save part of it or give it away.
          </p>
          <p className="lp-section-body">
            With Dough'Nate, they choose a charity from our list, tap Donate, and you complete the donation directly to the charity. You get the tax receipt.
          </p>
        </div>
      </section>

      {/* ───── PRICING ───── */}
      <section className="lp-section lp-pricing" data-testid="pricing-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Start free</h2>
          <div className="lp-pricing-cards">
            <div className="lp-price-card" data-testid="price-basic">
              <h3 className="lp-price-name">Basic — Free</h3>
              <p className="lp-section-body">
                One child, one device. Full monitoring, daily earning, cash payouts, Three Jars, Money School, medallions, and the Buck Shop's common collection.
              </p>
            </div>
            <div className="lp-price-card lp-price-card-featured" data-testid="price-family">
              <h3 className="lp-price-name">Family — CA$6.99/month, or CA$59.99/year</h3>
              <p className="lp-section-body">
                Everything in Basic, plus unlimited children and devices, a second parent, fitness rewards, the full Buck Shop, jar matching, Save-jar payouts, and advanced insights with a weekly digest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="lp-section lp-faq" data-testid="faq-section">
        <div className="container lp-section-inner">
          <h2 className="lp-section-h2">Questions</h2>
          <div className="lp-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`lp-faq-item ${openFaq === i ? 'lp-faq-item--open' : ''}`} data-testid={`faq-${i}`}>
                <button className="lp-faq-q" onClick={() => toggleFaq(i)} data-testid={`faq-toggle-${i}`}>
                  <span>{faq.q}</span>
                  <span className="lp-faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="lp-faq-a" data-testid={`faq-answer-${i}`}>
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── BOTTOM CTA ───── */}
      <section className="lp-final" data-testid="final-cta-section">
        <div className="container lp-final-inner">
          <h2 className="lp-final-h2">
            You fund it. They earn it.
          </h2>
          <StoreButtons src="footer" />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
